import React, { FC, useContext, useEffect, useMemo, useState } from 'react';
import { BdsTableCell, BdsTableRow, BdsTypo } from 'blip-ds/dist/blip-ds-react';
import { trackEvent } from '../../../../api/Billing/Tracking';
import { getMonthTwoDigits, toUTCDateString } from '../../../../utils/date';
import { PageHeaderComponent } from '../../components/Metrics/PageHeader/PageHeaderComponent';
import AdvancedDailyEngagedUsersContext from '../../../../contexts/Billing/AdvancedDailyEngagedUsersContext';
import { AdvancedDailyEngagedUserUsage } from '../../../../models/billing/advancedDailyEngagedUser/AdvancedDailyEngagedUserUsage';
import { AdvancedDailyEngagedUserPaper } from '../styles';
import FeatureToggleService from '../../../../services/FeatureToggleService';
import { BillingMetrics } from '../../../../shared/BillingMetrics';
import { REPORT_PERIOD_SELECT, origin } from '../../../../shared/SegmentEvents';
import BillingDailyEngagedUsersServiceApi from '../../../../api/Billing/BillingAdvancedDailyEngagedUserApi';
import TableWithPagination from '../../../../components/TableWithPagination/TableWithPagination';
import { useSortedPaginatedData } from '../../../../hooks/useSortedPaginatedData';
import { DEFAULT_PAGE_OPTIONS, COLUMN_WIDTHS_5 as COLUMN_WIDTHS } from '../../../../utils/paginationUtils';
import { buildAdvancedDeuMetricIds, useDeuAiEnabled } from './helpers';

export interface AdvancedDailyEngagedUserUsageProps {
  isDetailedView?: boolean;
}

const AdvancedDailyEngagedUserUsageComponent: FC<AdvancedDailyEngagedUserUsageProps> = ({ isDetailedView = false }) => {
  const { content, tenant, dateToSearch, loggedUser, trackingProps, getPlanStatus, periodOptions, setDateToSearch } =
    useContext(AdvancedDailyEngagedUsersContext);
  const isDeuAiEnabled = useDeuAiEnabled();

  const [dailyData, setDailyData] = useState<AdvancedDailyEngagedUserUsage[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const billingDailyEngagedUsersApi = new BillingDailyEngagedUsersServiceApi();

  const { itemsPerPage, sortConfig, sortedData, paginatedData, handlePaginationChange, handleItemsPerPageChange, handleSortChange } =
    useSortedPaginatedData({ data: dailyData });

  useEffect(() => {
    const getData = async () => {
      try {
        setIsLoading(true);
        const storageType = (await FeatureToggleService.isDatabricksStorageTypeEnabled(
          BillingMetrics.dailyEngagedUser,
        )) ? 1 : 0;

        const metricIds = buildAdvancedDeuMetricIds(isDeuAiEnabled);

        const monthString = `${dateToSearch.getFullYear()}-${getMonthTwoDigits(dateToSearch)}`;
        const data = await billingDailyEngagedUsersApi.getDailyAdvancedDailyEngagedUserConsumption(
          tenant.info.id,
          metricIds,
          monthString,
          -1,
          storageType,
        );

        setDailyData(data.map((obj) => ({
          ...obj,
          dateReference: toUTCDateString(obj.dateReference, content.locale),
        })));
      } finally {
        setIsLoading(false);
      }
    };

    getData();
  }, [dateToSearch, isDeuAiEnabled]);

  const periodOptionChangeEvent = async (selectedOption: any) => {
    const dateToRetrieveData = new Date(selectedOption.detail.value);
    setDateToSearch(dateToRetrieveData);
    const planStatus = await getPlanStatus();
    trackEvent(REPORT_PERIOD_SELECT, tenant, loggedUser, {
      ...trackingProps,
      origin: origin.ADVANCEDDEUPERDATE,
      date: dateToRetrieveData,
      planStatus,
    });
  };

  const tableRows = useMemo(
    () =>
      paginatedData.length > 0
        ? paginatedData.map((item) => (
            <BdsTableRow key={item.dateReference}>
              <BdsTableCell>
                <BdsTypo variant="fs-14" bold="bold" margin={false}>
                  {item.dateReference}
                </BdsTypo>
              </BdsTableCell>
              <BdsTableCell>
                <BdsTypo variant="fs-14" margin={false}>
                  {item.dailyEngagedUser.toLocaleString(content.locale)}
                </BdsTypo>
              </BdsTableCell>
              <BdsTableCell>
                <BdsTypo variant="fs-14" margin={false}>
                  {item.prospectConversation.toLocaleString(content.locale)}
                </BdsTypo>
              </BdsTableCell>
              <BdsTableCell>
                <BdsTypo variant="fs-14" margin={false}>
                  {isDeuAiEnabled ? item.dailyEngagedUserAI.toLocaleString(content.locale) : item.userConversation.toLocaleString(content.locale)}
                </BdsTypo>
              </BdsTableCell>
              <BdsTableCell>
                <BdsTypo variant="fs-14" margin={false}>
                  {item.activeMessage.toLocaleString(content.locale)}
                </BdsTypo>
              </BdsTableCell>
            </BdsTableRow>
          ))
        : undefined,
    [paginatedData, content.locale, isDeuAiEnabled],
  );

  let headings = [
    content.dailyConsumption.columnDateReference,
    content.dailyConsumption.columnDailyEngagedUsers,
    content.dailyConsumption.columnProspectConversations,
  ];

  let columnKeys = ['dateReference', 'dailyEngagedUser', 'prospectConversation'];

  headings = isDeuAiEnabled ? [...headings, content.dailyConsumption.columnAIAdditional] : [...headings, content.dailyConsumption.columnUserConversations];
  columnKeys = isDeuAiEnabled ? [...columnKeys, 'dailyEngagedUserAI'] : [...columnKeys, 'userConversation'];

  headings = [...headings, content.dailyConsumption.columnActiveMessages];
  columnKeys = [...columnKeys, 'activeMessage'];

  return (
    <>
      {isDetailedView && (
        <PageHeaderComponent
          title={content.dailyConsumption.title}
          dateToSearch={dateToSearch}
          periodOptions={periodOptions}
          periodOptionChangeEvent={periodOptionChangeEvent}
        />
      )}
      <AdvancedDailyEngagedUserPaper>
        {!isDetailedView && (
          <BdsTypo variant="fs-24" bold="bold" margin={false}>
            {content.dailyConsumption.title}
          </BdsTypo>
        )}
        <BdsTypo variant="fs-14" margin={false}>{content.dailyConsumption.about}</BdsTypo>
        <TableWithPagination
          options={{
            heading: headings,
            columnKeys,
            columnWidths: COLUMN_WIDTHS,
            sortConfig,
            onSortChange: handleSortChange,
            itemsPerPageList: DEFAULT_PAGE_OPTIONS,
            itemsPerPage,
            numberOfItems: isLoading ? itemsPerPage : sortedData.length,
            isLoading,
            hasEmptyState: !isLoading && sortedData.length === 0,
            emptyStateSubtitle: '',
            isAutoHeight: true,
          }}
          onPaginationChange={handlePaginationChange}
          onItemsPerPageChange={handleItemsPerPageChange}
        >
          {tableRows}
        </TableWithPagination>
      </AdvancedDailyEngagedUserPaper>
    </>
  );
};

export default AdvancedDailyEngagedUserUsageComponent;
